import type { ActivityLogItem } from '@/types';

import React from 'react';
import { Chip, Tooltip } from '@heroui/react';
import { useAtom, useAtomValue } from 'jotai';

import { TablePage, TableFilters, ColumnsToggleDropdown, FilterSelect } from '@/components/table';
import { activityLogsListParamsAtom, activityLogsQueryAtom } from '@/store';

// Custom hooks
import { useActivityLogsFilters, useActivityLogsSorting } from '@/hooks/activity-logs';
import { useVisibleColumns } from '@/hooks/table';

import { UserCell } from '@/components/shared/UserCell';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import { DomainCell } from '@/components/shared/DomainCell';
import { JsonViewer, JsonViewerPopover } from '@/components/shared/JsonViewer';

export default function ActivityLogsPage() {
  const { data, isLoading, isError, error } = useAtomValue(activityLogsQueryAtom);
  const [params, setParams] = useAtom(activityLogsListParamsAtom);

  const activityLogs = React.useMemo<ActivityLogItem[]>(() => data?.data ?? [], [data]);

  // Custom hooks
  const {
    logNameFilter,
    eventFilter,
    hasAnyFilter,
    clearFilters,
    handleLogNameChange,
    handleEventChange,
    handlePageChange
  } = useActivityLogsFilters();

  const { sortDescriptor, setSortDescriptor, sortedActivityLogs } = useActivityLogsSorting(activityLogs);

  // Function to trigger data refresh
  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  const getEventColor = (event: string): 'success' | 'warning' | 'danger' | 'primary' | 'default' => {
    switch (event) {
      case 'created':
        return 'success';
      case 'updated':
        return 'warning';
      case 'deleted':
        return 'danger';
      case 'unblocked':
        return 'success';
      default:
        return 'default';
    }
  };

  const getLogNameColor = (logName: string): 'primary' | 'secondary' | 'success' | 'warning' | 'danger' => {
    switch (logName) {
      case 'domain':
        return 'primary';
      case 'user':
        return 'secondary';
      case 'organization':
        return 'success';
      case 'experiment':
        return 'warning';
      case 'role':
        return 'danger';
      case 'blocked_users':
        return 'warning';
      default:
        return 'primary';
    }
  };

  const renderCell = React.useCallback((log: ActivityLogItem, columnKey: React.Key) => {
    switch (columnKey) {
      case 'id':
        return <span className="text-default-600 font-mono text-xs">{log.id}</span>;
      case 'log_name':
        return (
          <div className="flex flex-col">
            <Chip color={getLogNameColor(log.log_name) as any} size="sm" variant="flat">
              {log.log_name}
            </Chip>
            <p className="text-default-500 mt-1 text-xs">{log.description}</p>
          </div>
        );
      case 'performed_by':
        return log.causer ? <UserCell user={log.causer} dense linked /> : <span>-</span>;
      case 'event':
        return (
          <Chip color={getEventColor(log.event) as any} size="sm" variant="flat">
            {log.event}
          </Chip>
        );
      default:
        return null;
    }
  }, []);

  const renderCollapseContent = React.useCallback((log: ActivityLogItem) => {
    return (
      <div className="space-y-6 p-4">
        {/* Subject (What was affected) and Timestamp */}
        {log.subject && log.log_name === 'domain' && (
          <div className="grid grid-cols-2 gap-4">
            <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-4">
              <h4 className="text-default-700 dark:text-default-300 mb-3 text-sm font-semibold">Affected Domain</h4>
              <DomainCell
                domain={{
                  id: log.subject.id,
                  domain: log.subject.domain,
                  title: log.subject.title
                }}
                showTitle
                showID
              />
            </div>
            <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-4">
              <h4 className="text-default-700 dark:text-default-300 mb-3 text-sm font-semibold">Timestamp</h4>
              <DateTimeDisplay dateString={log.created_at} showSeconds />
            </div>
          </div>
        )}

        {/* Blocked Users unblock details */}
        {log.log_name === 'blocked_users' && log.properties && (
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-4">
            <h4 className="text-default-700 dark:text-default-300 mb-3 text-sm font-semibold">Unblocked User</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {log.properties.fingerprint && (
                <div>
                  <p className="text-default-500 dark:text-default-400 text-xs font-medium">Fingerprint</p>
                  <p className="text-default-700 dark:text-default-200 font-mono break-all">{log.properties.fingerprint}</p>
                </div>
              )}
              {log.properties.domain_id != null && (
                <div>
                  <p className="text-default-500 dark:text-default-400 text-xs font-medium">Domain ID</p>
                  <p className="text-default-700 dark:text-default-200 font-mono">{log.properties.domain_id}</p>
                </div>
              )}
              {log.properties.ip && (
                <div>
                  <p className="text-default-500 dark:text-default-400 text-xs font-medium">IP</p>
                  <p className="text-default-700 dark:text-default-200 font-mono">{log.properties.ip}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Timestamp for non-domain logs */}
        {(!log.subject || log.log_name !== 'domain') && (
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-4">
            <h4 className="text-default-700 dark:text-default-300 mb-3 text-sm font-semibold">Timestamp</h4>
            <DateTimeDisplay dateString={log.created_at} showSeconds />
          </div>
        )}

        {/* Properties (What changed) */}
        {log.properties && (log.properties.attributes || log.properties.old) && (
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-4">
            <h4 className="text-default-700 dark:text-default-300 mb-3 text-sm font-semibold">Changes</h4>
            <div className="space-y-3">
              {log.properties.old && (
                <div>
                  <p className="text-default-500 dark:text-default-400 mb-2 text-xs font-medium">Previous Values</p>
                  <JsonViewer
                    data={log.properties.old}
                    maxHeightPx={150}
                    unstyled
                    className="bg-red-50/50 p-2 dark:bg-red-900/10"
                  />
                </div>
              )}
              {log.properties.attributes && (
                <div>
                  <p className="text-default-500 dark:text-default-400 mb-2 text-xs font-medium">New Values</p>
                  <JsonViewer
                    data={log.properties.attributes}
                    maxHeightPx={150}
                    unstyled
                    className="bg-green-50/50 p-2 dark:bg-green-900/10"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subject details */}
        {log.subject && (
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-4">
            <h4 className="text-default-700 dark:text-default-300 mb-3 text-sm font-semibold">Subject Details</h4>
            <JsonViewerPopover data={log.subject} title="View Subject JSON" />
          </div>
        )}

        {/* Metadata */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">Subject Type</p>
            <p className="text-default-700 dark:text-default-200 font-mono text-sm">{log.subject_type}</p>
          </div>
          <div className="border-default-200 bg-default-50 dark:border-default-800 dark:bg-default-900/50 rounded-lg border p-3">
            <p className="text-default-500 dark:text-default-400 mb-1 text-xs font-medium">Subject ID</p>
            <p className="text-default-700 dark:text-default-200 font-mono text-sm">{log.subject_id}</p>
          </div>
        </div>
      </div>
    );
  }, []);

  const columns = React.useMemo(
    () => [
      { name: 'ID', uid: 'id', sortable: true, align: 'center', cellClass: 'text-center' },
      { name: 'Log Name', uid: 'log_name', sortable: true },
      { name: 'Performed By', uid: 'performed_by', sortable: false },
      { name: 'Event', uid: 'event', sortable: true }
    ],
    []
  );

  // Columns visibility management
  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={sortedActivityLogs}
      emptyMessage="No activity logs found"
      error={error}
      errorMessage="Failed to load activity logs"
      enableCollapse={true}
      renderCollapseContent={renderCollapseContent}
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={clearFilters}
          onRefresh={triggerRefresh}
        >
          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={(next) => setVisibleColumns(next)}
          />

          <FilterSelect
            label="Log Name"
            value={logNameFilter}
            options={[
              { key: 'all', label: 'All' },
              { key: 'domain', label: 'Domain' },
              { key: 'user', label: 'User' },
              { key: 'organization', label: 'Workspace' },
              { key: 'experiment', label: 'Experiment' },
              { key: 'role', label: 'Role' },
              { key: 'blocked_users', label: 'Blocked Users' }
            ]}
            onChange={handleLogNameChange}
          />

          <FilterSelect
            label="Event"
            value={eventFilter}
            options={[
              { key: 'all', label: 'All' },
              { key: 'created', label: 'Created' },
              { key: 'updated', label: 'Updated' },
              { key: 'deleted', label: 'Deleted' },
              { key: 'unblocked', label: 'Unblocked' }
            ]}
            onChange={handleEventChange}
          />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title="Activity Logs"
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(d) => setSortDescriptor(d)}
    />
  );
}
